<template>
  <div>
    <div v-if="!tagsOnly" class="flex align-items-baseline">
      <h3 class="m-b-sm m-t-none font-heavy">
        <span v-if="showEdit">Editing </span>Rosters
      </h3>
      <a
        v-if="canEdit && !showEdit"
        class="m-l-auto"
        @click.prevent="showEdit = true"
      >
        <i class="fa-solid fa-pencil color-gray-400 m-r-xxs"></i>Edit
      </a>
    </div>
    <div v-if="!showEdit">
      <div class="flex flex-wrap gap-x-xxs gap-y-xxs items-center m-y-none">
        <tag
          v-for="tag in rosterTagsWithCount(wrestlerProfile)"
          :tag="tag"
          :key="tag.abbreviation"
          :count="tag.count"
        ></tag>
        <a
          v-if="canEdit && showPencil && !showEdit"
          @click.prevent="showEdit = true"
        >
          <i class="fa-solid fa-pencil color-gray-400"></i>
        </a>
      </div>
      <div v-if="!tagsOnly" class="m-t-xxs">
        <div class="flex flex-wrap gap-xs align-items-center m-y-none">
          <VDropdown
            v-for="roster in visibleActiveRosters"
            :key="roster.id"
            theme="info-tooltip"
          >
            <!-- Trigger: bordered roster chip. Hover on desktop, tap on mobile (the info-tooltip theme includes a click trigger). -->
            <span
              class="inline-flex align-items-center rounded-xs border border-solid border-gray-300 bg-gray-50 text-gray-800 text-14"
              style="padding: 3px 8px; cursor: help"
              >{{ roster.name }}</span
            >
            <template v-slot:popper>
              <div style="max-width: 240px">
                {{ rosterAddedTooltip(roster) }}
              </div>
            </template>
          </VDropdown>
          <a
            v-if="hiddenActiveRosterCount > 0"
            class="text-14 text-gray-600"
            @click.prevent="showAllRosters = true"
          >
            + {{ hiddenActiveRosterCount }} more roster{{
              hiddenActiveRosterCount > 1 ? "s" : ""
            }}
            <i class="fa-solid fa-angle-down"></i>
          </a>
          <a
            v-else-if="showAllRosters && sortedActiveRosters.length > rosterCap"
            class="text-14 text-gray-600"
            @click.prevent="showAllRosters = false"
          >
            Show fewer <i class="fa-solid fa-angle-up"></i>
          </a>
        </div>
        <div v-if="archivedRosters(wrestlerProfile).length > 0" class="m-t-xs">
          <p v-if="!showingArchivedRosters">
            <a
              @click.prevent="showingArchivedRosters = !showingArchivedRosters"
            >
              {{ archivedRosters(wrestlerProfile).length }} archived roster{{
                archivedRosters(wrestlerProfile).length > 1 ? "s" : ""
              }}
              <i class="fa-solid fa-angle-down"></i>
            </a>
          </p>
          <div v-else-if="showingArchivedRosters">
            <h3 class="m-b-sm m-t-none font-heavy">
              <a
                @click.prevent="
                  showingArchivedRosters = !showingArchivedRosters
                "
                class="flex align-items-center gap-xs"
              >
                <span>Archived rosters</span> <i class="fa-solid fa-angle-up"></i>
              </a>
            </h3>
            <div class="flex flex-wrap gap-xs align-items-center m-y-none">
              <VDropdown
                v-for="roster in sortedArchivedRosters"
                :key="roster.id"
                theme="info-tooltip"
              >
                <span
                  class="inline-flex align-items-center rounded-xs border border-solid border-gray-200 bg-white text-gray-500 text-14"
                  style="padding: 3px 8px; cursor: help"
                  >{{ roster.name }}</span
                >
                <template v-slot:popper>
                  <div style="max-width: 240px">
                    {{ rosterAddedTooltip(roster) }}
                  </div>
                </template>
              </VDropdown>
            </div>
          </div>
        </div>
        <div
          v-if="archivedRosterTagsWithCount(wrestlerProfile).length > 0"
          class="m-t-xs"
        >
          >
          <p v-if="!showingArchivedTags">
            <a @click.prevent="showingArchivedTags = !showingArchivedTags">
              {{ archivedRosterTagsWithCount(wrestlerProfile).length }} archived
              roster tag{{
                archivedRosterTagsWithCount(wrestlerProfile).length > 1
                  ? "s"
                  : ""
              }}
              <i class="fa-solid fa-angle-down"></i>
            </a>
          </p>
          <div v-else-if="showingArchivedTags">
            <h3 class="m-b-sm m-t-none font-heavy">
              <a
                @click.prevent="showingArchivedTags = !showingArchivedTags"
                class="flex align-items-center gap-xs"
              >
                <span>Archived roster tags</span> <i class="fa-solid fa-angle-up"></i>
              </a>
            </h3>
            <div
              class="flex flex-wrap gap-x-xxs gap-y-xxs items-center m-y-none"
            >
              <tag
                v-for="tag in archivedRosterTagsWithCount(wrestlerProfile)"
                :tag="tag"
                :key="tag.abbreviation"
                :count="tag.count"
              ></tag>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <div v-if="tagsOnly" class="text-gray-600">
        Editing rosters for {{ wrestlerProfile.display_name }}
      </div>
      <wrestler-roster-edit
        :wrestler-id="wrestlerProfile.id"
        @cancel-roster-edit="showEdit = false"
        @rosters-updated="refreshWrestlerProfile"
      ></wrestler-roster-edit>
    </div>
    <p v-if="error">
      <small class="text-danger">{{ errorMessage }}</small>
    </p>
  </div>
</template>
<script>
import Tag from '../../shared/tag.vue';
import { taggableMix } from '../../shared/taggable_mix';
import { errorableMix } from './mixins/errorable_mix';
import WrestlerRosterEdit from './wrestler_roster_edit.vue';

export default {
  name: 'wrestler-rosters',
  components: {
    WrestlerRosterEdit,
    Tag,
  },
  mixins: [taggableMix, errorableMix],
  props: {
    canEdit: {
      type: Boolean,
      default: false,
    },
    showPencil: {
      type: Boolean,
      default: true,
    },
    forWrestlerProfile: {
      type: Object,
      required: true,
    },
    tagsOnly: {
      type: Boolean,
      default: false,
    },
    defaultExpanded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showEdit: false,
      wrestlerProfile: {},
      showingArchivedRosters: false,
      showingArchivedTags: false,
      showAllRosters: false,
    };
  },
  created() {
    this.wrestlerProfile = this.forWrestlerProfile;
    this.showEdit = this.defaultExpanded;
  },
  computed: {
    sortedActiveRosters() {
      return _.sortBy(this.activeRosters(this.wrestlerProfile), 'name');
    },
    sortedArchivedRosters() {
      return _.sortBy(this.archivedRosters(this.wrestlerProfile), 'name');
    },
    rosterCap() {
      return 12;
    },
    visibleActiveRosters() {
      if (this.showAllRosters) {
        return this.sortedActiveRosters;
      }
      return this.sortedActiveRosters.slice(0, this.rosterCap);
    },
    hiddenActiveRosterCount() {
      return Math.max(
        0,
        this.sortedActiveRosters.length - this.visibleActiveRosters.length,
      );
    },
  },
  methods: {
    rosterAddedTooltip(roster) {
      return (
        roster.added_note || "Join details weren't recorded for this roster."
      );
    },
    refreshWrestlerProfile() {
      // use Axios to hit the wrestler show endpoint with expand_rosters paramter set to true, then set wrestlerprofile to the result

      const url = this.$apiService.expandedRostersWrestlerUrl(
        this.wrestlerProfile.id,
      );
      axios
        .get(url)
        .then((response) => {
          this.wrestlerProfile = response.data;
          this.showEdit = false;
        })
        .catch((error) => {
          this.error = true;
          this.errorMessage = error.response.data.error;
        });
    },
  },
};
</script>
<style scoped></style>
